import {IDoppeViewerActionContext} from './doppe-viewer-action-context';
import {
    DoppeActionGoalType,
    doppeActionTypeSupportsOpeningListItemWidgetAsLink,
    WidgetRenderingContextType
} from '../../client-server-common/doppe-action-types/doppe-action-type';
import {
    DoppeActionListItemClickBehaviour,
    DoppeHideableValue,
    doppeHideableValueGetVisibleValue,
    doppeHideableValueIsVisibleAndNotEqualValue,
    DoppeHtmlSiteInfo,
    DoppeListActionSettings
} from '../../client-server-common';
import {DoppeActionWidgetPopupMode} from '../../client-server-common/types/doppe-action-widget-popup-mode';
import {
    DoppeViewerListItemInterstitialCouponModalPanel
} from '../../components/doppe-viewer-list-item-interstitial-coupon-modal-panel/doppe-viewer-list-item-interstitial-coupon-modal-panel';
import React from 'react';
import {
    evaluateFunction,
    identity,
    ObservableValue,
    observableValueCreateCalculated,
    urlTrimProtocol
} from '@wix/devzai-utils-common';
import {
    DoppeActionCTAButton,
    DoppeActionCTAType,
    DoppeActionCTATypeLinkOpenType,
    DoppeActionSettingsCTAButtonSettings
} from '../../client-server-common/types/doppe-action-cta-button';
import {
    DoppeViewerShareModalPanel
} from '../../components/doppe-viewer-share-modal-panel/doppe-viewer-share-modal-panel';
import {DoppeActionCtaButtonSettings} from '../../client-server-common/types/doppe-action-cta-button-settings';
import {
    DoppeActionContentSection,
    DoppeActionContentSectionType
} from '../../client-server-common/types/doppe-action-content-section';
import {
    DoppeActionTypeWidgetInnerDescription
} from '../../components/doppe-action-type-widget-inner-description/doppe-action-type-widget-inner-description';
import {DoppeViewerVideoPlayer} from '../../components/doppe-viewer-video-player/doppe-viewer-video-player';
import {DoppePageActionMediaView} from '../../components/doppe-page-action-media-view/doppe-page-action-media-view';
import {
    DoppeActionContentSectionPropertyDetails
} from '../../components/doppe-action-content-sections/doppe-action-content-section-property-details/doppe-action-content-section-property-details';
import {BymoPageViewerMode} from '../../components/bymo-page-viewer-context/bymo-page-viewer-context';
import {
    DoppeActionCheckoutModal
} from '../../doppe-action-types/doppe-actions-common/doppe-action-checkout-view/doppe-action-checkout-modal';
import {
    DoppeActionThankYouModal
} from '../../doppe-action-types/doppe-actions-common/doppe-action-thank-you-view/thankyou-view-modal';
import {
    DoppeActionCheckoutViewSettings
} from '../../doppe-action-types/doppe-actions-common/doppe-action-checkout-view/doppe-action-checkout-view';
import {
    DoppeDtoCheckoutIntent,
    doppeDtoCheckoutIntentCreateCustomFromCTAButton
} from '../../client-server-common/types/doppe-dto-checkout-intent';
import {
    DoppeActionContentSectionTestimonialsList
} from '../../components/doppe-action-content-sections/doppe-action-content-section-testimonials-list/doppe-action-content-section-testimonials-list';
import {
    DoppeActionContentSectionMediaGallery
} from '../../components/doppe-action-content-sections/doppe-action-content-section-media-gallery/doppe-action-content-section-media-gallery';
import {DoppeViewerLocalesAccessor} from '../../client/doppe-viewer-locales/doppe-viewer-locales';
import {DoppeViewerModalPanelLayout} from '../../components/doppe-viewer-modal-panel/doppe-viewer-modal-panel';
import {BymoPageLayout} from '../../components/bymo-page-style/bymo-page-style';
import {
    DoppeViewerModalContent,
    DoppeViewerModalOpeningAnimation,
} from '../../components/doppe-viewer-modals-controller/doppe-viewer-modals-controller';
import {
    doppeCheckoutActionSettingsCalculateFromAccountSettings
} from '../../client-server-common/types/doppe-checkout-action-settings';
import {DoppeViewerContentModal} from '../../components/doppe-viewer-content-modal/doppe-viewer-content-modal';
import {
    doppeActionCtaButtonTypeResolveURL
} from '../../doppe-action-types/doppe-actions-common/doppe-cta-button-utils/doppe-cta-button-utils';
import {
    DoppeActionModalIframeContent
} from '../../components/doppe-action-modal-iframe-content/doppe-action-modal-iframe-content';
import {classes, style} from './doppe-viewer-action-context-utils.st.css';

export function doppeViewerActionScrollToActionWhenInActionsList(
    actionContext: IDoppeViewerActionContext,
    options: {
        animated?: boolean;
        scrollOnlyWhenNotInViewport?: boolean;
    } = {}
) {
    const {
        animated = false,
        scrollOnlyWhenNotInViewport = false
    } = options;

    const actionsListController = actionContext.actionsListController;

    if (actionsListController) {
        actionsListController.scrollToAction(actionContext.action.id, {
            animated: animated,
            scrollOnlyWhenNotInViewport: scrollOnlyWhenNotInViewport
        }).catch(console.error);
    }
}

export function doppeViewerActionSetIsRenderingInnerState(
    actionContext: IDoppeViewerActionContext,
    isRenderingInnerState: boolean
) {
    actionContext.observableIsRenderingInnerState.setValue(isRenderingInnerState);

    doppeViewerActionScrollToActionWhenInActionsList(actionContext, {
        animated: false,
        scrollOnlyWhenNotInViewport: true
    });
}

export function doppeViewerActionIsRenderedInPage(
    actionContext: IDoppeViewerActionContext
) {
    const widgetRenderingContextType = actionContext.getWidgetRenderingContextType();

    return widgetRenderingContextType === WidgetRenderingContextType.Card || widgetRenderingContextType === WidgetRenderingContextType.Strip;
}

export function doppeViewerActionResolveListItemClickBehaviour<T extends DoppeListActionSettings.WithItemClickBehaviour>(
    actionContext: IDoppeViewerActionContext<T>
) {

    const actionType = actionContext.getActionType();
    doppeActionTypeSupportsOpeningListItemWidgetAsLink(actionType);

    const isRenderedInPage = doppeViewerActionIsRenderedInPage(actionContext);
    const itemClickBehaviour = actionContext.action.settings.itemClickBehaviour;

    const normalizedItemClickBehaviour =
        actionType.forceFullScreenOnListItemClick ? DoppeActionListItemClickBehaviour.PreviewItemAsPopup :
        !isRenderedInPage && itemClickBehaviour === DoppeActionListItemClickBehaviour.PreviewItemAsPopup ?
        DoppeActionListItemClickBehaviour.PreviewItem :
        itemClickBehaviour;

    return normalizedItemClickBehaviour === DoppeActionListItemClickBehaviour.OpenLink && !doppeActionTypeSupportsOpeningListItemWidgetAsLink(actionContext.getActionType()) ?
        DoppeActionListItemClickBehaviour.PreviewItem :
        normalizedItemClickBehaviour;
}

export function doppeViewerActionHandleListItemClick<T extends DoppeListActionSettings.WithItemClickBehaviour>(
    actionContext: IDoppeViewerActionContext<T>,
    options: {
        itemId: string;
        itemIndex: number;
        itemLink?: string;
        couponCode?: DoppeHideableValue<string>;
        customItemClickBehaviour?: (itemClickBehaviour: DoppeActionListItemClickBehaviour) => DoppeActionListItemClickBehaviour;
        widgetPopupMode?: DoppeActionWidgetPopupMode;
        showHeader?: boolean;
    }
) {
    const {
        itemId,
        itemIndex,
        itemLink,
        couponCode,
        customItemClickBehaviour = identity,
        widgetPopupMode,
        showHeader = true
    } = options;

    const observableCurrentItemIdToRender = actionContext.observableCurrentItemIdToRender;

    const itemClickBehaviour = customItemClickBehaviour(doppeViewerActionResolveListItemClickBehaviour(actionContext));

    switch (itemClickBehaviour) {
        case DoppeActionListItemClickBehaviour.PreviewItem: {
            doppeViewerActionSetIsRenderingInnerState(actionContext, true);
            observableCurrentItemIdToRender?.setValue(itemId);

            actionContext.reportInnerItemPreviewed({
                innerItemId: itemId,
                innerItemPosition: itemIndex,
                previewType: DoppeActionListItemClickBehaviour.PreviewItem
            });

            break;
        }
        case DoppeActionListItemClickBehaviour.PreviewItemAsPopup: {
            actionContext.previewAction(actionContext.action.id, {
                listItemId: itemId,
                widgetPopupMode: widgetPopupMode ?? DoppeActionWidgetPopupMode.Drawer,
                actionIndex: actionContext.getActionIndex(),
                startNewContext: true,
                showHeader: showHeader,
            })

            actionContext.reportInnerItemPreviewed({
                innerItemId: itemId,
                innerItemPosition: itemIndex,
                previewType: DoppeActionListItemClickBehaviour.PreviewItemAsPopup
            });

            break;
        }
        case DoppeActionListItemClickBehaviour.OpenLink: {
            if (itemLink) {
                if (couponCode && doppeHideableValueIsVisibleAndNotEqualValue(couponCode, '')) {
                    actionContext.modalsController.showInnerModal({
                        content: (
                            <DoppeViewerListItemInterstitialCouponModalPanel
                                couponCode={couponCode}
                                itemLink={itemLink}
                                actionContext={actionContext}
                                itemId={itemId}
                                itemIndex={itemIndex}
                            />
                        )
                    })
                } else {
                    actionContext.reportGoalAchieved({
                        goalType: DoppeActionGoalType.Click,
                        innerItemId: itemId,
                        innerItemPosition: itemIndex

                    });
                    actionContext.navigateToUrl(itemLink, true);
                }
            }

            break;
        }
    }
}


export function doppeViewerActionHandleShareWhenNavigatorIsNotSupported(
    actionContext: IDoppeViewerActionContext<DoppeActionCtaButtonSettings>,
    options: {
        sourceURL?: string,
        htmlSiteInfo?: DoppeHtmlSiteInfo
    }
) {

    const {
        sourceURL,
        htmlSiteInfo
    } = options;

    if (actionContext.action.settings.ctaButton.settings.type === DoppeActionCTAType.Share) {
        actionContext.modalsController.showInnerModal({
            content: (
                <DoppeViewerShareModalPanel
                    link={doppeActionCtaButtonTypeResolveURL(actionContext.action.settings.ctaButton.settings, {
                        sourceURL: sourceURL,
                        htmlSiteInfo: htmlSiteInfo
                    }) ?? ''}
                    actionContext={actionContext}
                    title={actionContext.action.settings.ctaButton.settings.title}
                    description={actionContext.action.settings.ctaButton.settings.description}
                />
            )
        })
    }
}

export function doppeViewerActionOpenCheckoutFlow(options: {
    actionContext: IDoppeViewerActionContext<any>,
    observableCheckoutDetails: ObservableValue<DoppeActionCheckoutViewSettings>,
    innerItemId?: string,
    onCheckoutCompletion: (options: {
        paymentIntentId: string,
        consent?: boolean,
        message?: string
    }) => void;
    isCustomAmount?: boolean;
    ignoreNotify?: boolean;
    viewerLocales?: DoppeViewerLocalesAccessor;
}) {

    const {
        actionContext,
        observableCheckoutDetails,
        innerItemId,
        onCheckoutCompletion,
        isCustomAmount = false,
        ignoreNotify = true,
    } = options;

    void actionContext.performContextCallback(async options => {

        const {
            viewerContextData,
            clientServices
        } = options;

        const checkoutIntent = observableCheckoutDetails.getValue().checkoutIntent;

        const priceData = checkoutIntent.price;

        const createStripePaymentIntent = async () => {
            const result = await actionContext.createStripePaymentIntent({
                checkoutIntent: checkoutIntent,
                innerItemId: innerItemId,
            });

            if (viewerContextData.mode === BymoPageViewerMode.Live) {
                clientServices.viewerAnalyticsClient.logEvent('uouCreatePaymentIntent', {
                    ...actionContext.getBiIdFields(),
                    value: `${priceData.amount}`,
                    currency: priceData.currencyCode,
                    noteAdded: false,
                    isCustomAmount: isCustomAmount,
                    paymentIntentId: result.paymentIntentId,
                });
            }

            return result;
        }

        const saveCheckoutIntent = (options: {
            uouCheckoutData: DoppeDtoCheckoutIntent.UouCheckoutData,
            paymentIntentId: string
        }) => {
            const checkoutIntent = observableCheckoutDetails.getValue().checkoutIntent;

            return actionContext.saveCheckoutIntent({
                checkoutIntent: checkoutIntent,
                uouCheckoutData: options.uouCheckoutData,
                paymentIntentId: options.paymentIntentId,
                innerItemId: innerItemId,
            });
        };

        actionContext.modalsController.showInnerModal({
            content: ({modalController}) => (
                <DoppeActionCheckoutModal
                    dismissButtonCallback={() => {
                        modalController.closeWindow();
                    }}
                    onDone={({
                                 name,
                                 paymentIntentId,
                                 consent,
                                 message
                             }) => {

                        if (!ignoreNotify) {
                            clientServices
                                .getApiClient()
                                .notifyPaymentSuccess({
                                    paymentIntentId,
                                    uouMessage: message,
                                    emailSubscriptionConsent: consent,
                                })
                                .catch(console.error);
                        }


                        modalController.closeWindowImmediately();
                        onCheckoutCompletion?.({
                            paymentIntentId: paymentIntentId,
                            consent: consent,
                            message: message
                        });

                        const checkoutViewSettings = observableCheckoutDetails.getValue();
                        actionContext.modalsController.showInnerModal({
                            content: ({modalController}) => (
                                <DoppeActionThankYouModal
                                    dismissButtonCallback={() => {
                                        modalController.closeWindow();
                                    }}
                                    onConfirm={() => {
                                        modalController.closeWindow();
                                    }}
                                    name={name}
                                    price={checkoutViewSettings.checkoutIntent.price}
                                    customThankYouMessage={checkoutViewSettings.checkoutIntent.checkoutSettings.customThankYouMessage}
                                />
                            )
                        })
                    }}
                    actionContext={actionContext}
                    observableCheckoutDetails={observableCheckoutDetails}
                    createStripePaymentIntent={createStripePaymentIntent}
                    saveCheckoutIntent={saveCheckoutIntent}
                    demoMode={viewerContextData.mode !== BymoPageViewerMode.Live}
                    isCustomAmount={false}
                />
            )
        })

    })
}

export function doppeViewerActionCtaButtonTypeResolveOnClick(ctaButton: DoppeActionCTAButton.BaseType & {
    text: DoppeHideableValue<string>
}, options: {
    observableButtonSettings: ObservableValue<DoppeActionSettingsCTAButtonSettings>;
    ctaCallback: ((settings: DoppeActionSettingsCTAButtonSettings) => void) | undefined;
    actionContext: IDoppeViewerActionContext<any>;
    listItemId?: string;
    viewerLocales: DoppeViewerLocalesAccessor;
    htmlSiteInfo?: DoppeHtmlSiteInfo;
}) {

    const {
        ctaCallback,
        actionContext,
        observableButtonSettings,
        listItemId,
        viewerLocales,
        htmlSiteInfo
    } = options;

    const ctaSettings = ctaButton.settings;


    switch (ctaSettings.type) {
        case DoppeActionCTAType.Call:
        case DoppeActionCTAType.Email:
        case DoppeActionCTAType.Whatsapp:
        case DoppeActionCTAType.Navigate:
        case DoppeActionCTAType.FacebookMessage: {
            return undefined;
        }
        case DoppeActionCTAType.InstagramMessage: {
            return undefined;
        }
        case DoppeActionCTAType.Link:
        case DoppeActionCTAType.IsracardPay:
        case DoppeActionCTAType.OpenPage:
        case DoppeActionCTAType.OpenProduct: {
            const isLink = ctaSettings.type === DoppeActionCTAType.Link;

            if (ctaSettings.openType === DoppeActionCTATypeLinkOpenType.InPage) {
                return (sourceURL?: string) => {
                    doppeViewerActionContextOpenModal(
                        actionContext,
                        () => {
                            return {
                                render: () =>
                                    <DoppeActionModalIframeContent
                                        withMinHeight={!isLink}
                                        src={doppeActionCtaButtonTypeResolveURL(observableButtonSettings.getValue(), {
                                            sourceURL: sourceURL,
                                            htmlSiteInfo: htmlSiteInfo
                                        }) ?? ''}
                                    />
                                ,
                                dependencies: [observableButtonSettings]
                            }
                        },
                        {
                            mobileLayout: isLink ? DoppeViewerModalPanelLayout.InnerFull : DoppeViewerModalPanelLayout.Drawer,
                            desktopLayout: DoppeViewerModalPanelLayout.InnerFull,
                            showHeader: true,
                            showHeaderAsCloseButton: !isLink,
                            title: isLink ? urlTrimProtocol(doppeActionCtaButtonTypeResolveURL(ctaButton.settings, {
                                sourceURL: sourceURL,
                                htmlSiteInfo: htmlSiteInfo
                            }) ?? '') : ''
                        }
                    );
                }
            } else {
                return undefined;
            }
        }

        case DoppeActionCTAType.Share: {
            return (sourceURL?: string) => {
                if (navigator.share) {
                    void evaluateFunction(async () => {
                        try {
                            await navigator.share({
                                title: ctaSettings.title,
                                url: doppeActionCtaButtonTypeResolveURL(ctaSettings, {
                                    sourceURL: sourceURL,
                                    htmlSiteInfo: htmlSiteInfo
                                }),
                                text: ctaSettings.description,
                            })
                        } catch (e) {
                            console.error(e);
                        }
                    })
                } else {
                    doppeViewerActionHandleShareWhenNavigatorIsNotSupported(actionContext, {
                        sourceURL: sourceURL,
                        htmlSiteInfo: htmlSiteInfo
                    })
                }

                ctaCallback?.(ctaSettings);
            };
        }
        case DoppeActionCTAType.Checkout: {
            return () => {
                const observableCheckoutDetails = observableValueCreateCalculated(() => {
                    const buttonSettings = observableButtonSettings.getValue() as DoppeActionSettingsCTAButtonSettings.Checkout;
                    const actualCheckoutSettings = doppeCheckoutActionSettingsCalculateFromAccountSettings(buttonSettings.checkoutSettings, buttonSettings.price.currencyCode);

                    return {
                        checkoutIntent: doppeDtoCheckoutIntentCreateCustomFromCTAButton({
                            ctaButtonSettings: buttonSettings,
                            checkoutSettings: actualCheckoutSettings,
                        }),
                        checkoutTitle: doppeHideableValueGetVisibleValue(buttonSettings.checkoutSettings.title, doppeHideableValueGetVisibleValue(ctaButton.text, viewerLocales['checkoutTitle'])),
                    } as DoppeActionCheckoutViewSettings;
                }, [observableButtonSettings]);

                doppeViewerActionOpenCheckoutFlow({
                    actionContext: actionContext,
                    observableCheckoutDetails: observableCheckoutDetails,
                    innerItemId: listItemId,
                    onCheckoutCompletion: () => {
                        actionContext.reportGoalAchieved({
                            goalType: DoppeActionGoalType.Payment,
                            innerItemId: listItemId
                        })
                    },
                    ignoreNotify: false,
                    viewerLocales: viewerLocales
                });

                ctaCallback?.(ctaSettings);
            };
        }
        case DoppeActionCTAType.SaveContact: {
            return () => {
                ctaCallback?.(ctaSettings);
            };
        }
    }
}


export function doppeViewerActionContentSectionTypeResolveContent(contentSection: DoppeActionContentSection, actionContext: IDoppeViewerActionContext) {

    const {
        content,
    } = contentSection;

    switch (content.type) {
        case DoppeActionContentSectionType.RichContent: {
            return <DoppeActionTypeWidgetInnerDescription
                maxLines={Infinity}
            >
                {content.richText}
            </DoppeActionTypeWidgetInnerDescription>
        }
        case DoppeActionContentSectionType.Video: {
            return <DoppeViewerVideoPlayer
                className={style(classes.mediaRoot, {})}
                videoResource={content.video}
                muteVideo={false}
                autoplay={content.autoPlay}
                videoTitle={contentSection.title}
            />
        }
        case DoppeActionContentSectionType.Image: {
            return <DoppePageActionMediaView
                className={style(classes.mediaRoot, {})}
                mediaResource={content.image}
                mediaViewSettings={content.imageViewSettings}
                widthSpec={{contentRelativeWidth: 1}}
            />
        }
        case DoppeActionContentSectionType.PropertyDetails: {
            return <DoppeActionContentSectionPropertyDetails
                contentSection={content}
            />
        }
        case DoppeActionContentSectionType.Testimonials: {
            return <DoppeActionContentSectionTestimonialsList
                contentSection={content}
                actionContext={actionContext}
            />
        }
        case DoppeActionContentSectionType.Gallery: {
            return <DoppeActionContentSectionMediaGallery
                contentSection={content}
                actionContext={actionContext}
            />
        }
    }
}


export function doppeViewerActionContextOpenModal(
    actionContext: IDoppeViewerActionContext,
    content: DoppeViewerModalContent,
    options?: {
        onClosed?: () => void;
        widgetPopupMode?: DoppeActionWidgetPopupMode;
        withoutOverlay?: boolean;
        showHeader?: boolean;
        showHeaderAsCloseButton?: boolean;
        title?: string;
        mobileLayout?: DoppeViewerModalPanelLayout;
        desktopLayout?: DoppeViewerModalPanelLayout;
    }) {

    const {
        onClosed,
        withoutOverlay,
        showHeader = true,
        showHeaderAsCloseButton = false,
        title = '',
        mobileLayout = DoppeViewerModalPanelLayout.FullScreen,
        desktopLayout = DoppeViewerModalPanelLayout.Inner,
    } = options ?? {};

    actionContext.modalsController.showInnerModal({
        onClosed: onClosed,
        withoutOverlay: withoutOverlay,
        openingAnimation: bymoPageLayout => {
            const modalPanelLayout = bymoPageLayout === BymoPageLayout.Wide ?
                desktopLayout :
                mobileLayout;

            switch (modalPanelLayout) {
                case DoppeViewerModalPanelLayout.Drawer: {
                    return DoppeViewerModalOpeningAnimation.SlideInFromBottom;
                }
                default: {
                    return DoppeViewerModalOpeningAnimation.FadeIn;
                }
            }
        },
        content: ({modalController}) => (
            <DoppeViewerContentModal
                closeCallback={() => {
                    modalController.closeWindow()
                }}
                mobileLayout={mobileLayout}
                desktopLayout={desktopLayout}
                content={content}
                showHeader={showHeader}
                showHeaderAsCloseButton={showHeaderAsCloseButton}
                title={title}
            />
        )
    })
}
